import { Box, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IPositionType } from 'src/generated-types'
import { ConfigureView } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { ConfigurePalette } from './configurePalette'

const LIST_HINT_KEYS = ['configure-hint-click', 'configure-hint-shift', 'configure-hint-esc']

const getDrawingHintKeys = (activeType: IPositionType) => {
  return [
    'configure-hint-click',
    'configure-hint-drag',
    'configure-hint-wheel',
    'configure-hint-pan',
    'configure-hint-alt',
    ...(activeType !== IPositionType.MooringLine ? ['configure-hint-cage'] : []),
    'configure-hint-esc',
  ]
}

interface DrawingHintsProps {
  view: ConfigureView
  activeType: IPositionType
  palette: ConfigurePalette
}

const DrawingHints: React.FC<DrawingHintsProps> = ({ view, activeType, palette }) => {
  const { t } = useTranslation(['position', 'projects'])
  const hintKeys = view === 'list' ? LIST_HINT_KEYS : getDrawingHintKeys(activeType)

  return (
    <>
      <HintRow>
        {hintKeys.map(key => (
          <span key={key}>{t(key, { ns: 'position' })}</span>
        ))}
      </HintRow>
      <HintRow sx={{ gap: '6px 16px' }}>
        {view === 'drawing' && (
          <>
            <LegendItem>
              <LegendLine lineColor={palette.unconfigured} dashed />
              {t('unconfigured', { ns: 'projects' })}
            </LegendItem>
            <LegendItem>
              <LegendLine lineColor={palette.primary} />
              {t('legend-selected', { ns: 'position' })}
            </LegendItem>
          </>
        )}
      </HintRow>
    </>
  )
}

export default DrawingHints

const HintRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 14px',
  fontSize: 12,
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const LegendItem = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}))

const LegendLine = styled('i', {
  shouldForwardProp: prop => prop !== 'lineColor' && prop !== 'dashed',
})<{ lineColor: string; dashed?: boolean }>(({ lineColor, dashed }) => ({
  width: 20,
  borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${lineColor}`,
}))
